"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useIsLoggedIn } from "@/components/shared/is-logged-in";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import type { PostType } from "@/contexts/feed-context";
import { useFeedInternalContext } from "@/contexts/feed-context";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import type { UseGetPostMethod } from "@/hooks-api/use-get-posts";
import {
  feedFreezeDebugLog,
  getFeedFreezeDebugDuration,
} from "@/utils/feed-freeze-debug";
import { isAbortError, parseError } from "@/utils/helpers";

import { FeedLoginWall } from "./feed/feed-login-wall";
import { FeedPostItem } from "./feed/feed-post-item";
import { getFeedFilters } from "./get-feed-filters";

const MAX_NON_LOGGED_IN_POSTS = 25;

type FeedProps = {
  getPosts: UseGetPostMethod;
  userPublicKey?: string;
  showAdminPinnedPosts?: boolean;
  scrollRestorationKey?: string;
};

export const Feed = ({
  userPublicKey,
  getPosts,
  showAdminPinnedPosts,
  scrollRestorationKey = "default-feed",
}: FeedProps) => {
  const {
    posts = [],
    allPostsLength,
    setPosts,
    postCategoryFilter,
    postOrderBy,
    adminPinnedPosts,
    getScrollPosition,
    setScrollPosition,
  } = useFeedInternalContext();
  const [loading, setLoading] = useState(false);
  const [noMoreItems, setNoMoreItems] = useState(false);
  const [failedOffset, setFailedOffset] = useState<number | null>(null);
  const isLoggedIn = useIsLoggedIn();
  const postCategoryFilterKey = JSON.stringify(postCategoryFilter);
  const feedRequestKey = useMemo(
    () =>
      JSON.stringify({
        postCategoryFilter: postCategoryFilterKey,
        postOrderBy,
        scrollRestorationKey,
        userPublicKey: userPublicKey ?? null,
      }),
    [postCategoryFilterKey, postOrderBy, scrollRestorationKey, userPublicKey],
  );
  const activeFeedRequestKeyRef = useRef<string | null>(feedRequestKey);

  const { schedulePostBodyCacheWarmup } = usePostCacheContext();

  const { ref: loadMoreRef, isIntersecting: shouldLoadMore } =
    useIntersectionObserver({
      rootMargin: "2000px",
      threshold: 0,
    });

  const loadMoreItems = useCallback(async () => {
    if (loading) {
      feedFreezeDebugLog("feed", "loadMore:skipLoading", {
        allPostsLength,
        feedRequestKey,
      });
      return;
    }

    if (failedOffset === allPostsLength) {
      feedFreezeDebugLog("feed", "loadMore:skipFailedOffset", {
        allPostsLength,
        failedOffset,
        feedRequestKey,
      });
      return;
    }

    const requestFeedKey = activeFeedRequestKeyRef.current;

    // Prevent loading more for non-logged in users who have reached their limit
    if (!isLoggedIn && allPostsLength >= MAX_NON_LOGGED_IN_POSTS) {
      feedFreezeDebugLog("feed", "loadMore:skipAnonymousLimit", {
        allPostsLength,
        feedRequestKey,
      });
      setNoMoreItems(true);
      return;
    }

    setLoading(true);
    const startedAt = performance.now();

    const filters = getFeedFilters({
      userPublicKey,
      postCategoryFilter,
    });

    feedFreezeDebugLog("feed", "loadMore:start", {
      allPostsLength,
      feedRequestKey,
      filters,
      postOrderBy,
      requestFeedKey,
      userPublicKey: userPublicKey ?? null,
    });

    try {
      const { posts: postNodes } = await getPosts({
        filters,
        orderBy: postOrderBy,
        offset: allPostsLength,
      });

      if (requestFeedKey !== activeFeedRequestKeyRef.current) {
        feedFreezeDebugLog("feed", "loadMore:dropStaleResult", {
          activeFeedRequestKey: activeFeedRequestKeyRef.current,
          count: postNodes.length,
          durationMs: getFeedFreezeDebugDuration(startedAt),
          requestFeedKey,
        });
        return;
      }

      feedFreezeDebugLog("feed", "loadMore:received", {
        count: postNodes.length,
        durationMs: getFeedFreezeDebugDuration(startedAt),
        firstPostHash: postNodes[0]?.postHash ?? null,
        lastPostHash: postNodes.at(-1)?.postHash ?? null,
        offset: allPostsLength,
      });

      if (postNodes.length === 0) {
        setNoMoreItems(true);
        return;
      }

      postNodes.forEach(({ body, postHash, extraData }) => {
        const safeBody = body?.trim() ?? "";
        const isMarkdown = extraData?.isMarkdown === "true";

        schedulePostBodyCacheWarmup({
          cacheKey: safeBody || postHash!,
          body: safeBody,
          isMarkdown,
          priority: "idle",
        });

        if (
          safeBody.length > 4000 ||
          !!extraData?.EmbedVideoURL ||
          !!extraData?.DisabledEmbedURLs
        ) {
          feedFreezeDebugLog(
            "feed",
            "warmup:queuePostBody",
            {
              bodyLength: safeBody.length,
              hasDisabledEmbedUrls: !!extraData?.DisabledEmbedURLs,
              hasEmbedVideoUrl: !!extraData?.EmbedVideoURL,
              isMarkdown,
              postHash: postHash ?? null,
            },
            {
              throttleKey: `feed-precache:${postHash ?? body}`,
              throttleMs: 2_000,
            },
          );
        }
      });

      feedFreezeDebugLog("feed", "loadMore:applyPosts", {
        count: postNodes.length,
        durationMs: getFeedFreezeDebugDuration(startedAt),
        offset: allPostsLength,
        uniqueHashes: new Set(postNodes.map((post) => post.postHash)).size,
      });

      setPosts((state: PostType[]) => {
        const existingPostHashes = new Set(state.map((post) => post.postHash));
        const uniqueNewPosts = postNodes.filter(
          (post) => !existingPostHashes.has(post.postHash),
        );
        return [...state, ...uniqueNewPosts];
      });
      setFailedOffset(null);
    } catch (error) {
      if (requestFeedKey !== activeFeedRequestKeyRef.current) {
        feedFreezeDebugLog("feed", "loadMore:dropStaleError", {
          activeFeedRequestKey: activeFeedRequestKeyRef.current,
          durationMs: getFeedFreezeDebugDuration(startedAt),
          requestFeedKey,
        });
        return;
      }

      if (isAbortError(error)) {
        return;
      }

      const parsedError = parseError(error);

      feedFreezeDebugLog(
        "feed",
        "loadMore:error",
        {
          durationMs: getFeedFreezeDebugDuration(startedAt),
          error: parsedError,
          offset: allPostsLength,
          postOrderBy,
          userPublicKey: userPublicKey ?? null,
        },
        { level: "error" },
      );

      setFailedOffset(allPostsLength);
      toast({
        variant: "destructive",
        title: "Failed to load more posts",
        description: "More posts could not be loaded. Please try again.",
        details: createToastErrorDetails(error),
      });
    } finally {
      if (requestFeedKey === activeFeedRequestKeyRef.current) {
        setLoading(false);
      }
    }
  }, [
    allPostsLength,
    failedOffset,
    feedRequestKey,
    getPosts,
    isLoggedIn,
    loading,
    postCategoryFilter,
    postOrderBy,
    schedulePostBodyCacheWarmup,
    setPosts,
    userPublicKey,
  ]);

  useEffect(() => {
    feedFreezeDebugLog("feed", "feedRequestKey:changed", {
      feedRequestKey,
      previousFeedRequestKey: activeFeedRequestKeyRef.current,
    });

    activeFeedRequestKeyRef.current = feedRequestKey;
    setFailedOffset(null);
    setNoMoreItems(false);
    setLoading(false);

    return () => {
      activeFeedRequestKeyRef.current = null;
    };
  }, [feedRequestKey]);

  useEffect(() => {
    window.scrollTo(0, getScrollPosition(scrollRestorationKey));

    const handleScroll = () => {
      setScrollPosition(scrollRestorationKey, window.scrollY);
    };

    document.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [getScrollPosition, scrollRestorationKey, setScrollPosition]);

  useEffect(() => {
    if (shouldLoadMore && !noMoreItems && !loading) {
      feedFreezeDebugLog("feed", "intersection:loadMore", {
        allPostsLength,
        feedRequestKey,
        noMoreItems,
      });
      loadMoreItems();
      return;
    }

    if (shouldLoadMore) {
      feedFreezeDebugLog("feed", "intersection:skip", {
        allPostsLength,
        feedRequestKey,
        loading,
        noMoreItems,
      });
    }
  }, [
    allPostsLength,
    feedRequestKey,
    shouldLoadMore,
    loadMoreItems,
    noMoreItems,
    loading,
  ]);

  const finalPosts = showAdminPinnedPosts
    ? [...adminPinnedPosts, ...posts]
    : posts;

  const limitedPosts = isLoggedIn
    ? finalPosts
    : finalPosts.slice(0, MAX_NON_LOGGED_IN_POSTS);

  useEffect(() => {
    if (!isLoggedIn && finalPosts.length >= MAX_NON_LOGGED_IN_POSTS) {
      setNoMoreItems(true);
    }
  }, [finalPosts.length, isLoggedIn]);

  return (
    <div className="relative">
      {limitedPosts.map((post, index) =>
        post.postHash ? (
          <FeedPostItem
            post={post}
            // Note: we can have duplicated posts = Pinned post can duplicate regular one
            key={post.postHash + index}
            index={index}
          />
        ) : null,
      )}

      {!isLoggedIn && finalPosts.length >= MAX_NON_LOGGED_IN_POSTS ? (
        <FeedLoginWall />
      ) : (
        <div className="relative h-5 w-full" ref={loadMoreRef} />
      )}
    </div>
  );
};
